import { useState, useRef, useEffect } from 'react';
import { MoreVertical, Flag, Link as LinkIcon } from 'lucide-react';
import { useProfileStore } from '../../stores/profileStore';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../context/ModalContext';
import { useProfileRole } from '../../hooks/useProfileRole';

interface ReportButtonMenuProps {
  onReport: () => void;
  onCopySuccess?: () => void;
  entityOwner: string;
  contentTypeTitle: string;
}

export function BottomSuccessMessage() {
  const { t } = useTranslation('reports');
  return (
    <div className="fixed bottom-6 right-6 bg-green-600 text-white px-4 py-2 rounded-lg shadow-xl animate-fade-in">
      {t('link_copied', { ns: 'reports' })}
    </div>
  );
}

export default function ReportButtonMenu({
  onReport,
  onCopySuccess,
  entityOwner,
  contentTypeTitle,
}: ReportButtonMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { profile } = useProfileStore();

  const { t } = useTranslation('reports');

  const { isAdmin } = useProfileRole();

  const canMakeReport = entityOwner === profile?.id;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      onCopySuccess?.();
    } catch (err) {
      console.error('Erreur lors de la copie du lien', err);
    }
  };

  const { setIsAnyModalOpen } = useModal();

  const closeModal = () => {
    onReport();
    setIsAnyModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      {!canMakeReport && !isAdmin && (
        <button
          onClick={() => setOpen(prev => !prev)}
          className="absolute top-0 right-2 bottom-6 md:right-0 text-secondary hover:text-primary"
          aria-label="Ouvrir le menu"
        >
          <MoreVertical className="h-4 w-4 rounded-full hover:bg-gray-100 hover:scale-110 transition-all duration-200 cursor-pointer" />
        </button>
      )}

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg border-2xl border border-gray-100 z-50 animate-fade-in">
          <ul className="py-1 text-sm text-gray-700">
            <li>
              <button
                onClick={() => {
                  setIsAnyModalOpen(false);
                  closeModal();
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-warning hover:bg-red-50 transition"
                aria-label={t('report')}
              >
                <Flag size={16} />
                {t(contentTypeTitle)}
              </button>
            </li>

            <li>
              <button
                onClick={() => {
                  handleCopyLink();
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100 transition"
                aria-label={t('copy_link')}
              >
                <LinkIcon size={16} />
                {t('copy_link')}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
